import { FC } from "react";
import { useTranslation } from 'react-i18next';
import { useBranding } from "@utils/api";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { GuidedBookingProvider, useGuidedBookingContext } from "@contexts/GuidedBookingContext";
import { Stepper } from "@/components/ui";
import { Clock } from "lucide-react";

// Import components
import WizardHeader from "./WizardHeader";
import AppointmentSelectionStep from "./AppointmentSelectionStep";
import BookingConfirmationStep from "./BookingConfirmationStep";
import CompletionStep from "./CompletionStep";
import WizardNavigation from "./WizardNavigation";

type GuidedBookingWizardProps = {
  onClose?: () => void;
}

// Inner component that uses the GuidedBookingContext
const GuidedBookingWizardContent: FC<GuidedBookingWizardProps> = ({ onClose }) => {
  const { t, i18n } = useTranslation();
  const { branding, isLoading, isError } = useBranding();
  
  const { 
    currentStep, 
    isCurrentStep,
    getStepTitle,
    tokenData,
    isTokenValid
  } = useGuidedBookingContext();


  const steps = {
    appointmentSelection: { description: '', title: getStepTitle('appointmentSelection') },
    bookingConfirmation: { description: '', title: getStepTitle('bookingConfirmation') },
    completionStep: { description: '', title: '' }
  };

  // Show token expiration error first
  if (tokenData && !isTokenValid) {
    return (
      <Card className="w-full max-w-lg bg-white p-6 rounded-2xl text-center">
        <CardContent className="space-y-6 p-0">
          <div className="text-center p-4 space-y-4">
            <Clock className="h-16 w-16 text-wb-sienna mx-auto" />
            <h2 className="text-xl font-medium text-wb-sienna">
              {t('guidedBooking.tokenExpired.title', { defaultValue: 'Booking Link Expired' })}
            </h2>
            <p className="text-gray-600">
              {t('guidedBooking.tokenExpired.message', { 
                defaultValue: 'This booking link has expired. Please request a new booking link to continue.' 
              })}
            </p>
            {onClose && (
              <button
                onClick={onClose}
                className="mt-4 px-6 py-2 bg-gray-500 text-white rounded-lg hover:bg-gray-600 transition-colors"
              >
                {t('common.close', { defaultValue: 'Close' })}
              </button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  // Show loading state while waiting for data or token parsing
  if (isLoading || !tokenData) {
    return <Skeleton className="w-full h-64" />;
  }
  
  // Show error state
  if (!branding) {
    return (
      <div className="text-center p-4">
        <p className="text-red-500">Unable to load booking form</p>
      </div>
    );
  }

  return (
    <Card 
      role="application" 
      className="w-full max-w-lg bg-white p-6 rounded-2xl text-center"
      aria-label={t('aria.guidedBookingMessage', { 
        clinicName: branding.name
      })}
    >
      <CardContent className="space-y-6 p-0">
        <WizardHeader 
          onClose={onClose}
          currentStep={currentStep}
          supportedLanguages={branding?.supportedLanguages}
        />

        {!isCurrentStep('complete') && (
          <div className="w-full mx-auto space-y-4">
            <div>
              <h1 className="text-lg sm:text-xl font-medium pb-2">
                {t('guidedBooking.title')}
              </h1>
              <div className="pb-2">
                <Stepper steps={steps} activeStep={currentStep} />
              </div>

              {/* Appointment Selection Step */}
              {isCurrentStep('appointmentSelection') && (
                <AppointmentSelectionStep />
              )}

              {/* Booking Confirmation Step */}
              {isCurrentStep('bookingConfirmation') && (
                <BookingConfirmationStep />
              )}

              <div className="mb-2"></div>
              {/* Navigation */}
              <WizardNavigation
                language={i18n.language}
              />
            </div>
          </div>
        )}

        {/* Completion Step */}
        {isCurrentStep('complete') && (
          <CompletionStep />
        )}
      </CardContent>
    </Card>
  );
};

// Main wrapper component with GuidedBookingProvider
const GuidedBookingWizard: FC<GuidedBookingWizardProps> = ({ onClose }) => {
  return (
    <GuidedBookingProvider>
      <GuidedBookingWizardContent onClose={onClose} />
    </GuidedBookingProvider>
  );
};

export default GuidedBookingWizard;